/**
 * Cart recommendations (THEME.md §7.15; T-64): the element of `blocks/okt-cart-recommendations.liquid`.
 *
 * - Loads the block's cards once it is on screen (in the drawer: when it opens), the way Horizon's
 *   product recommendations load (`assets/product-recommendations.js`): it fetches the parent section
 *   from the recommendations endpoint and takes this block's content from the response.
 * - The element has `data-skip-subtree-update`, so Horizon's drawer and cart page refreshes keep the
 *   loaded cards (`assets/morph.js`). A refresh still updates the element's attributes: a new base
 *   product (`data-product-id`) loads the cards again.
 * - Without products, or when the request fails, the block hides (`data-count="0"`).
 */

const CONTENT_SELECTOR = '[data-okt-cart-recs-content]';

class OktCartRecommendations extends HTMLElement {
  /**
   * The request whose cards are shown
   * @type {string | null}
   */
  #loadedUrl = null;

  /** @type {AbortController | null} */
  #abortController = null;

  #intersectionObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this.#intersectionObserver.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver(() => this.#queueLoad());

  connectedCallback() {
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-url', 'data-section-id', 'data-product-id', 'data-intent', 'data-limit'],
    });
    this.#queueLoad();
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * The recommendations request for the current attributes; null without a base product
   * @returns {string | null}
   */
  get #url() {
    const { url, sectionId, productId, intent, limit } = this.dataset;
    if (!url || !sectionId || !productId) return null;

    const params = new URLSearchParams({
      product_id: productId,
      section_id: sectionId,
      intent: intent || 'related',
      limit: limit || '4',
    });
    return `${url}?${params}`;
  }

  /**
   * Loads the cards once the element is on screen, unless they're the current ones already
   */
  #queueLoad() {
    const url = this.#url;
    if (!url || url === this.#loadedUrl) return;

    this.#intersectionObserver.disconnect();
    this.#intersectionObserver.observe(this);
  }

  async #load() {
    const url = this.#url;
    if (!url || url === this.#loadedUrl) return;

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const html = await response.text();
      const content = findById(new DOMParser().parseFromString(html, 'text/html'), this.id)?.querySelector(
        CONTENT_SELECTOR
      );
      if (!content) throw new Error('No block content in the response');

      this.#show(content, url);
    } catch (error) {
      if (abortController.signal.aborted) return;

      console.error('Cart recommendations error:', error instanceof Error ? error.message : error);
      const empty = document.createElement('div');
      empty.setAttribute('data-okt-cart-recs-content', '');
      empty.dataset.count = '0';
      this.#show(empty, url);
    } finally {
      if (this.#abortController === abortController) this.#abortController = null;
    }
  }

  /**
   * Replaces the shown content (loading state or earlier cards)
   * @param {Element} content
   * @param {string} url
   */
  #show(content, url) {
    this.#fitImages(content);
    const next = document.importNode(content, true);
    const current = this.querySelector(CONTENT_SELECTOR);

    if (current) {
      current.replaceWith(next);
    } else {
      this.append(next);
    }
    this.#loadedUrl = url;
  }

  /**
   * Horizon's card gallery sizes its images for a page grid ('50vw' from 750 px) and loads the first
   * one eagerly. Before the parsed cards reach the page, their images become lazy (slides off screen
   * wait), and in a carousel narrower than 750 px (the block's container query) they take the card
   * width as their size: the block's width shared as its cards share it (D-122 CU3, T-193), by Columns
   * from 750 px and by Mobile columns' span of 12 below. Grid cards (Style "Grid", T-144) keep Horizon's
   * sizes.
   * @param {Element} content
   */
  #fitImages(content) {
    const list = this.querySelector('.block-resource-list') ?? this;
    const width = list.clientWidth;
    const narrow = width > 0 && width < 750;
    const style = getComputedStyle(list);
    const columns = parseFloat(style.getPropertyValue('--column-count')) || 3;
    const span = parseFloat(style.getPropertyValue('--okt-card-span')) || 5;
    const cardWidth = `${Math.ceil(matchMedia('(min-width: 750px)').matches ? width / columns : (width * span) / 12)}px`;

    for (const image of content.querySelectorAll('img')) {
      image.setAttribute('loading', 'lazy');
      if (narrow && image.hasAttribute('srcset') && image.closest('.resource-list__carousel')) {
        image.setAttribute('sizes', cardWidth);
      }
    }
  }
}

/**
 * Finds an element by id in a parsed response, including template content: the empty-cart zone is
 * also rendered inside the drawer's `<template id="empty-cart-template">`.
 * @param {Document} doc
 * @param {string} id
 * @returns {Element | null}
 */
function findById(doc, id) {
  const element = doc.getElementById(id);
  if (element) return element;

  for (const template of doc.querySelectorAll('template')) {
    const inTemplate = template.content.getElementById(id);
    if (inTemplate) return inTemplate;
  }
  return null;
}

if (!customElements.get('okt-cart-recommendations')) {
  customElements.define('okt-cart-recommendations', OktCartRecommendations);
}
